import { ethers } from "ethers";
import {
  decodePrivateSwap,
  privateSwapDeployment,
  privateSwapRouteHash,
  PRIVATE_SWAP_INTERFACE,
} from "./privateSwapConfig";
import { recoverFlatJoinSplitV2SwapOutput } from "./swapOutput";
import {
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementBroadcast,
  type FlatJoinSplitV2JournalRecord,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import { readFlatJoinSplitV2TreeHistory } from "./treeHistory";
import { PinnedHistory } from "./pinnedHistory";
import { requestPrivateFees } from "../utils/relayer-client";

// Share a canonical, incremental public event stream across every wallet swap.
const swapHistory = new PinnedHistory<ethers.Log[]>(
  (logs) => logs.length <= 100_000,
);
const recoveryAttempts = new Map<string, number>();

export async function reconcilePrivateSwapJournal(
  provider: ethers.Provider,
  scope: FlatJoinSplitV2JournalScope,
  record: FlatJoinSplitV2JournalRecord,
  safeBlock: number,
  safeBlockHash: string,
  fromBlock: number,
) {
  const current = decodePrivateSwap(record.transaction.data),
    deployment = privateSwapDeployment();
  if (!current || !deployment || !record.swapOutputSeed)
    throw Error("Invalid swap recovery record");
  const hash = privateSwapRouteHash(current.route),
    topics = [
      PRIVATE_SWAP_INTERFACE.getEvent("PrivateSwapCompleted")!.topicHash,
      PRIVATE_SWAP_INTERFACE.getEvent("PrivateSwapRecovered")!.topicHash,
    ];
  const history = await swapHistory.read({
    provider: provider as any,
    key: deployment.coordinator,
    block: safeBlock,
    hash: safeBlockHash,
    build: async (previous) => {
      const logs = [...(previous?.value || [])];
      for (
        let start = previous ? previous.block + 1 : deployment.block;
        start <= safeBlock;
        start += 2000
      )
        logs.push(
          ...(await provider.getLogs({
            address: deployment.coordinator,
            topics: [topics],
            fromBlock: start,
            toBlock: Math.min(start + 1999, safeBlock),
          })),
        );
      return logs;
    },
    validate: (logs) => {
      if (
        logs.some(
          (log) =>
            log.removed ||
            ethers.getAddress(log.address) !== deployment.coordinator ||
            !topics.includes(log.topics[0]) ||
            log.blockNumber < deployment.block ||
            log.blockNumber > safeBlock,
        )
      )
        throw Error("Invalid swap history");
    },
  });
  const logs = history.filter((log) => log.topics[1] === hash);
  if (!logs.length) {
    const block = await provider.getBlock(safeBlock);
    if (block?.hash !== safeBlockHash)
      throw Error("Swap recovery confirmation changed");
    if (
      block.timestamp > Number(current.route.deadline) &&
      Date.now() - (recoveryAttempts.get(hash) || 0) > 60_000
    ) {
      const source = new ethers.Contract(
        record.poolAddress,
        ["function nullifierUsed(bytes32) view returns(bool)"],
        provider,
      );
      const spent = await Promise.all(
        record.sourceNullifiers.map((n) =>
          source.nullifierUsed(n, { blockTag: safeBlock }),
        ),
      );
      if (spent.length && spent.every(Boolean)) {
        recoveryAttempts.set(hash, Date.now());
        while (recoveryAttempts.size > 256)
          recoveryAttempts.delete(recoveryAttempts.keys().next().value!);
        const result = await requestPrivateFees<{ transactionHash: string }>(
          "swap-resume",
          { id: current.leg.quote.nonce },
          { pool: record.poolAddress },
        );
        await markFlatJoinSplitV2SettlementBroadcast(
          scope,
          record.id,
          result.transactionHash,
        );
      }
    }
    return null;
  }
  if (logs.length !== 1 || logs[0].removed)
    throw Error("Ambiguous swap confirmation");
  const [receipt, tx] = await Promise.all([
    provider.getTransactionReceipt(logs[0].transactionHash),
    provider.getTransaction(logs[0].transactionHash),
  ]);
  if (
    !receipt ||
    !tx ||
    receipt.status !== 1 ||
    tx.to !== deployment.coordinator ||
    tx.value !== 0n ||
    Number(tx.chainId) !== 84532 ||
    receipt.blockNumber > safeBlock ||
    receipt.blockHash !== logs[0].blockHash
  )
    throw Error("Invalid swap receipt");
  const decoded = decodePrivateSwap(tx.data);
  const original = PRIVATE_SWAP_INTERFACE.parseTransaction({
    data: record.transaction.data,
  })!;
  if (
    !decoded ||
    PRIVATE_SWAP_INTERFACE.encodeFunctionData(
      decoded.recovery ? "recover" : "execute",
      original.args,
    ).toLowerCase() !== tx.data.toLowerCase()
  )
    throw Error("Recovered transaction differs from the accepted swap");
  const block = await provider.getBlock(receipt.blockNumber);
  if (block?.hash !== receipt.blockHash)
    throw Error("Swap confirmation changed");
  const source = new ethers.Contract(
    record.poolAddress,
    ["function nullifierUsed(bytes32) view returns(bool)"],
    provider,
  );
  if (
    !(
      await Promise.all(
        record.sourceNullifiers.map((n) =>
          source.nullifierUsed(n, { blockTag: safeBlock }),
        ),
      )
    ).every(Boolean)
  )
    throw Error("Swap input is not spent");
  const output = await recoverFlatJoinSplitV2SwapOutput({
    provider,
    receipt,
    pool: record.poolAddress,
    chainId: record.chainId,
    calldata: tx.data,
    seed: record.swapOutputSeed,
  });
  const located = await Promise.all(
    record.outputNotes.map(async (note) => {
      if (note.commitment === record.swapOutputCommitment) return output;
      const history = await readFlatJoinSplitV2TreeHistory({
        provider: provider as any,
        pool: record.poolAddress,
        treeIndex: note.treeIndex!,
        fromBlock,
        toBlock: safeBlock,
        expectedToBlockHash: safeBlockHash,
        chunkSize: 5000,
      });
      const insertion = history.insertions.find(
        (n) => n.commitment?.toLowerCase() === note.commitment.toLowerCase(),
      );
      if (!insertion || insertion.blockNumber > receipt.blockNumber)
        throw Error("Swap change is not confirmed");
      return { ...note, leafIndex: insertion.leafIndex };
    }),
  );
  return markFlatJoinSplitV2SettlementFinalized(scope, record.id, {
    txHash: receipt.hash,
    blockNumber: receipt.blockNumber,
    blockHash: receipt.blockHash,
    outputNotes: located,
    swapOutputNote: output,
    swapRecovered: decoded.recovery,
  });
}
